import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { FAQPageSchema } from '@/components/feature/SEOHead';

const ABOUT_FAQ = [
  {
    question: '茄子视频是什么公司？',
    answer: '茄子视频是领先的聚合型在线影视平台，致力于整合西瓜视频、糖豆视频、小蓝视频、芒果视频、丝瓜视频、蓝莓视频等多个视频源，为用户提供一站式的视频内容浏览和观看服务。我们以技术创新为驱动，以用户体验为核心，打造最便捷的在线影视聚合平台。',
  },
  {
    question: '茄子视频的内容来源有哪些？',
    answer: '茄子视频聚合了六大主流视频平台的内容，包括西瓜视频（搞笑娱乐）、糖豆视频（舞蹈健身）、小蓝视频（科技知识）、芒果视频（热门综艺）、丝瓜视频（热播剧集）和蓝莓视频（纪录探索），覆盖多种内容类型，满足不同用户的观看需求。',
  },
];

const MILESTONES = [
  { year: '2024', title: '平台创立', description: '茄子视频正式上线，确定聚合型在线影视平台的定位' },
  { year: '2025', title: '快速成长', description: '完成六大视频频道整合，月活用户突破1000万' },
  { year: '2026', title: '行业领先', description: '累计用户超过5000万，成为在线影视聚合领域的标杆平台' },
];

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    const formData = new FormData(form);
    const submitAddr = 'https://readdy.ai/api/form/d9mu1iu8mbnljin59rk0';

    try {
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 茄子视频 | 品牌介绍与联系方式"
        description="茄子视频是领先的聚合型在线影视平台，整合西瓜视频、糖豆视频、小蓝视频、芒果视频、丝瓜视频、蓝莓视频等视频源。了解我们的品牌故事、发展历程和联系方式。"
        keywords="茄子视频,关于我们,品牌介绍,在线影视平台,联系方式"
        canonicalUrl="https://qiezi.video/about"
      />
      <FAQPageSchema questions={ABOUT_FAQ} />

      <main>
        <Navbar />

        {/* Hero */}
        <section className="pt-20 pb-12 md:pb-16 bg-background-100">
          <div className="w-full px-4 md:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
              关于茄子视频
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              聚合在线影视平台，汇集西瓜视频、糖豆视频、小蓝视频、芒果视频、丝瓜视频、蓝莓视频等优质内容，为亿万用户提供高清免费在线观看服务
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20creative%20team%20collaborating%20around%20a%20large%20screen%20showing%20video%20content%2C%20warm%20natural%20lighting%2C%20plants%20and%20wooden%20furniture%2C%20innovative%20tech%20company%20culture%2C%20friendly%20and%20professional%20atmosphere&width=800&height=500&seq=about-brand-001&orientation=landscape"
                  alt="茄子视频团队"
                  className="w-full rounded-lg object-cover"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  茄子视频创立于2024年，创始团队来自互联网和视频行业的资深从业者，怀揣着"让每个人都能轻松享受视频内容"的愿景，打造了这个聚合型在线影视平台。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  我们深知用户在多个视频平台之间来回切换的烦恼，因此整合了西瓜视频、糖豆视频、小蓝视频、芒果视频、丝瓜视频、蓝莓视频六大主流视频源，覆盖搞笑娱乐、舞蹈健身、科技知识、热门综艺、热播剧集和纪录探索等多种内容类型。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  目前，茄子视频已拥有超过5000万用户，日均访问量突破千万，成为国内领先的在线影视聚合平台。我们将继续努力，为用户提供更丰富的内容和更优质的体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-8 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '用户至上',
                  desc: '以用户需求为导向，持续优化产品体验，让每一位用户都能轻松找到想看的内容。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '品质保障',
                  desc: '严格筛选内容来源，确保所有视频均为高清画质，提供流畅的观看体验。',
                },
                {
                  icon: 'ri-lightbulb-flash-line',
                  title: '技术创新',
                  desc: '持续投入技术研发，运用AI推荐算法，为用户智能推荐最感兴趣的内容。',
                },
                {
                  icon: 'ri-team-line',
                  title: '合作共赢',
                  desc: '与各大视频平台建立深度合作关系，共同构建健康繁荣的在线视频生态。',
                },
              ].map((value) => (
                <div key={value.title} className="p-5 md:p-6 rounded-lg bg-background-50 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50">
                    <i className={`${value.icon} text-primary-600 text-xl md:text-2xl`}></i>
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 font-heading">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-8 text-center">
              发展历程
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary-200 hidden sm:block"></div>
                <div className="flex flex-col gap-6">
                  {MILESTONES.map((m, idx) => (
                    <div key={m.year} className="flex items-start gap-6">
                      <div className="hidden sm:flex w-16 h-16 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-lg font-heading relative z-10">
                        {m.year}
                      </div>
                      <div className="flex-1 pt-0 sm:pt-4">
                        <span className="sm:hidden text-sm font-bold text-primary-600 mb-1 block">{m.year}</span>
                        <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-1 font-heading">{m.title}</h3>
                        <p className="text-sm text-foreground-600">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-8 text-center">
                有任何问题或合作意向，欢迎随时联系我们
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮件', value: 'contact@qiezi.video' },
                    { icon: 'ri-phone-line', title: '客服电话', value: '400-888-8888' },
                    { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区科技园区创新路88号' },
                    { icon: 'ri-time-line', title: '工作时间', value: '周一至周五 9:00 - 18:00' },
                  ].map((info) => (
                    <div key={info.title} className="flex items-start gap-4 p-4 rounded-lg bg-background-50">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50">
                        <i className={`${info.icon} text-primary-600 text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950 mb-1">{info.title}</h4>
                        <p className="text-sm text-foreground-600">{info.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact Form */}
                <div className="p-5 md:p-6 rounded-lg bg-background-50">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-4 font-heading">发送消息</h3>
                  {formSubmitted ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-green-50">
                        <i className="ri-check-line text-green-500 text-2xl"></i>
                      </div>
                      <h4 className="text-base font-semibold text-foreground-950 mb-2">消息已发送</h4>
                      <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复您！</p>
                      <button
                        onClick={() => setFormSubmitted(false)}
                        className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                      >
                        发送新消息
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} data-readdy-form="">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      <div className="flex flex-col gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            value={contactForm.name}
                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                            placeholder="请输入您的姓名"
                            className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            placeholder="请输入您的邮箱地址"
                            className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            maxLength={500}
                            value={contactForm.message}
                            onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                            placeholder="请输入您的留言内容（最多500字）"
                            rows={4}
                            className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                          />
                          <p className="text-xs text-foreground-500 mt-1">{contactForm.message.length}/500</p>
                        </div>
                        {formError && (
                          <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{formError}</p>
                        )}
                        <button
                          type="submit"
                          className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-primary-600 cursor-pointer"
                        >
                          发送消息
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-8 text-center">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {ABOUT_FAQ.map((faq, idx) => (
                <AboutFAQItem key={idx} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}

function AboutFAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg bg-background-100 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between cursor-pointer text-left"
      >
        <span className="text-sm md:text-base font-medium text-foreground-950 pr-4">{question}</span>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 text-lg flex-shrink-0 transition-transform duration-300`}></i>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-48 pb-4' : 'max-h-0'}`}>
        <p className="px-5 text-sm text-foreground-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}